import {getTestProps} from '@benchling/js-test-utils';
import {Badge, createStyles, Flex, Group, Text} from '@mantine/core';
import React, {useCallback} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import type {
  CanvasVersionFileStateQuery,
  CanvasVersionFileStateQueryVariables,
} from 'canvas-core/backbone/__generated/CanvasVersionFileStateQuery';
import {
  CANVAS_VERSION_FILE_STATE_QUERY,
  CanvasVersionFileStateGraphQL,
} from 'canvas-core/backbone/CanvasVersionGraphQL';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {CenteredSpinner} from 'ui-library/Spinner';

export interface InlineCanvasCardProps {
  canvasDefinitionId: string;
  canvasVersionId: string;
}

const useStyles = createStyles((theme) => ({
  card: {
    width: '100%',
    marginTop: theme.spacing.md,
    marginBottom: theme.spacing.md,
    border: `1px solid ${theme.colors.gray[2]}`,
    borderRadius: theme.radius.sm,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.md,
  },
  iconTile: {
    alignItems: 'center',
    backgroundColor: theme.colors.blue[0],
    borderRadius: theme.radius.sm,
    color: theme.colors.blue[8],
    display: 'flex',
    height: 32,
    justifyContent: 'center',
    width: 32,
    flexShrink: 0,
  },
  title: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,
    lineHeight: 1.3,
  },
  actions: {
    alignItems: 'center',
    flexShrink: 0,
    gap: theme.spacing.xs,
  },
  loadingCard: {
    minHeight: 48,
  },
}));

function formatCanvasVersionLabel(versionIndex: number): string {
  return `v${versionIndex}`;
}

function CanvasCardContent({
  canvasDefinitionId,
  canvasVersionId,
  canvasVersion,
}: {
  canvasDefinitionId: string;
  canvasVersionId: string;
  canvasVersion: CanvasVersionFileStateGraphQL;
}): JSX.Element {
  const {classes} = useStyles();

  const handleOpen = useCallback(() => {
    FileBrowserActions.openFile(canvasDefinitionId, {versionApiId: canvasVersionId});
  }, [canvasDefinitionId, canvasVersionId]);

  return (
    <Flex className={classes.card} {...getTestProps({component: 'InlineCanvasCard', element: 'root'})}>
      <Group align="center" noWrap={true} spacing="sm">
        <div className={classes.iconTile}>
          <BenchlingIcon icon="canvas_definition" />
        </div>
        <Group align="center" noWrap={true} spacing="xs">
          <Text className={classes.title}>{canvasVersion.definition.name}</Text>
          <Badge color="gray" size="xs" variant="light">
            {formatCanvasVersionLabel(canvasVersion.versionIndex)}
          </Badge>
        </Group>
      </Group>
      <Group className={classes.actions} noWrap={true} spacing="xs">
        <Button
          buttonStyle={ButtonStyle.SECONDARY_WHITE}
          size={ButtonSize.SMALL}
          icon="expand-right-full"
          onClick={handleOpen}
          aria-label="Open canvas"
          {...getTestProps({component: 'InlineCanvasCard', element: 'openButton'})}
        />
      </Group>
    </Flex>
  );
}

export default function InlineCanvasCard({
  canvasDefinitionId,
  canvasVersionId,
}: InlineCanvasCardProps): JSX.Element {
  const {classes} = useStyles();
  const queryResult = useSkippableBenchlingQuery<
    CanvasVersionFileStateQuery,
    CanvasVersionFileStateQueryVariables
  >(CANVAS_VERSION_FILE_STATE_QUERY, {
    skip: !BenchlingConfig.ENABLE_CANVAS_CORE,
    variables: {canvasVersionId},
  });

  if (queryResult.type === QueryResultType.SKIPPED) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Canvas Core is not enabled." />;
  }

  if (queryResult.type === QueryResultType.PENDING) {
    return (
      <Flex align="center" className={`${classes.card} ${classes.loadingCard}`} justify="center">
        <CenteredSpinner />
      </Flex>
    );
  }

  if (queryResult.type === QueryResultType.ERROR) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load canvas details." />;
  }

  const {canvasVersion} = queryResult.data;
  if (isInaccessible(canvasVersion)) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load canvas details." />;
  }

  if (canvasVersion.definition.id !== canvasDefinitionId) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load canvas details." />;
  }

  return (
    <CanvasCardContent
      canvasDefinitionId={canvasDefinitionId}
      canvasVersionId={canvasVersionId}
      canvasVersion={canvasVersion}
    />
  );
}
